import { useState, useEffect } from 'react';
import { Field, useFormikContext } from 'formik';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Tab,
  Tabs,
  Avatar,
  IconButton,
  useTheme,
  Stack,
} from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import { Button } from '../../../../shared/components/ui/Button';
import { basicInfoValidationSchema, personalInfoValidationSchema, profileValidationSchema } from '../../validations/userValidationSchema';
import { useAuth } from '../../../auth';
import { GENDER_OPTIONS, SECTION_OPTIONS, UNIT_OPTIONS, USER_ROLES, USER_STATUS } from '../../constants/userConstants';
import { useTenant } from '../../../../shared/context/TenantContext';
import { useUser } from '../../context/useUser';
import { useQuery } from '@tanstack/react-query';
import { sectorService } from '../../../../shared/services/sector/sectorService';
import { unitService } from '../../../../shared/services/unit/unitService';

interface UserFormProps {
  mode: 'create' | 'edit';
  loading?: boolean;
  onCancel?: () => void;
}


const MaskedField = ({ mask, ...props }: any) => {
  const formatValue = (value: string) => {
    if (!value) return '';

    const numbers = value.replace(/\D/g, '');

    if (mask === '(99) 99999-9999') {
      if (numbers.length <= 2) return numbers;
      if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }

    if (mask === '999.999.999-99') {
      if (numbers.length <= 3) return numbers;
      if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
      if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
      return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
    }

    return value;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const formattedValue = formatValue(rawValue);

    if (props.onChange) {
      props.onChange({
        target: {
          name: props.name,
          value: formattedValue
        }
      });
    }
  };

  return (
    <TextField
      {...props}
      onChange={handleChange}
      value={props.value || ''}
    />
  );
};

export default function UserForm({
  mode,
  loading = false,
  onCancel,
}: UserFormProps) {
  const theme = useTheme();
  const { user: authUser } = useAuth();
  const { selectedTenant, handleSetSelectedTenant } = useTenant();
  const [tab, setTab] = useState(0);
  const [avatar, setAvatar] = useState<string | null>(null);

  const { values, errors, touched, setFieldValue, isValid } = useFormikContext<any>();


  useEffect(() => {
    if (values.imageUrl && !avatar) {
      setAvatar(values.imageUrl);
    }
    if (values.tenantId && !selectedTenant) {
      handleSetSelectedTenant(values.tenantId);
    }
  }, [values.imageUrl, values.tenantId, avatar, selectedTenant, handleSetSelectedTenant]);

  console.log('UserForm - values:', values);
  console.log('UserForm - avatar:', avatar);

  const handleTabChange = (_: any, newValue: number) => setTab(newValue);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const url = URL.createObjectURL(file);
      setAvatar(url);
      setFieldValue('avatarFile', file);
      setFieldValue('imageUrl', url);
    }
  };

  const validateStep = (currentTab: number) => {
    try {
      if (currentTab === 0) {
        basicInfoValidationSchema.validateSync(values, { abortEarly: false });
      } else if (currentTab === 1) {
        profileValidationSchema.validateSync(values, { abortEarly: false });
      } else if (currentTab === 2) {
        personalInfoValidationSchema.validateSync(values, { abortEarly: false });
      }
      return true;
    } catch (err) {
      console.log('Validation error for tab', currentTab, err);
      return false;
    }
  };

  const handleNext = () => {
    if (validateStep(tab)) {
      setTab(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setTab(prev => prev - 1);
  };


  const { data: sectors = [] } = useQuery({
    queryKey: ['sectors', selectedTenant],
    queryFn: async () => {
      if (!selectedTenant) return [];
      const res = await sectorService.list({ tenantId: selectedTenant.trim() });
      return res.data;
    },
    enabled: !!selectedTenant,
  });


  const { data: units = [] } = useQuery({
    queryKey: ['units', values.section_name],
    queryFn: async () => {
      if (!values.section_name) return [];
      const res = await unitService.list({ sectorId: values.section_name });
      return res.data;
    },
    enabled: !!values.section_name,
  });

  return (
    <Box sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
      <Box sx={{ borderBottom: 1, borderColor: theme.palette.divider, mb: 3 }}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '.MuiTab-root': {
              color: theme.palette.text.secondary,
            },
            '.Mui-selected': {
              color: theme.palette.primary.main,
            },
          }}
        >
          <Tab label="Informações Básicas" />
          <Tab label="Perfil e Permissões" />
          <Tab label="Informações Pessoais" />
        </Tabs>
      </Box>

      {/* Tab 0 - Informações Básicas */}
      {tab === 0 && (
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                  src={avatar || values.imageUrl}
                  sx={{ width: 80, height: 80, mr: 2, bgcolor: theme.palette.primary.light }}
                >
                  {values.name?.[0]?.toUpperCase()}
                </Avatar>
                <div>
                  <Typography variant="body2" color={theme.palette.text.secondary} mb={1}>
                    Foto do usuário
                  </Typography>
                  <label htmlFor="avatar-upload">
                    <input
                      accept="image/*"
                      id="avatar-upload"
                      type="file"
                      style={{ display: 'none' }}
                      onChange={handleAvatarChange}
                    />
                    <IconButton color="primary" component="span">
                      <PhotoCamera />
                    </IconButton>
                  </label>
                </div>
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Field
                as={TextField}
                fullWidth
                label="Nome completo"
                name="name"
                required
                disabled={loading}
                error={!!(touched.name && errors.name)}
                helperText={touched.name && errors.name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                as={TextField}
                fullWidth
                label="E-mail"
                name="email"
                type="email"
                required
                disabled={loading}
                error={!!(touched.email && errors.email)}
                helperText={touched.email && errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field name="documentNumber">
                {({ field }: any) => (
                  <MaskedField
                    {...field}
                    fullWidth
                    label="CPF"
                    mask="999.999.999-99"
                    required
                    disabled={loading}
                    error={!!(touched.documentNumber && errors.documentNumber)}
                    helperText={touched.documentNumber && errors.documentNumber}
                  />
                )}
              </Field>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                as={TextField}
                select
                fullWidth
                label="Status"
                name="status"
                required
                disabled={loading}
                error={!!(touched.status && errors.status)}
                helperText={touched.status && errors.status}
              >
                {USER_STATUS.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Field>
            </Grid>
          </Grid>
        </Box>
      )}


      {tab === 1 && (
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Field
                as={TextField}
                select
                fullWidth
                label="Tipo de usuário"
                name="role"
                required
                disabled={loading}
                error={!!(touched.role && errors.role)}
                helperText={touched.role && errors.role}
                sx={{
                  bgcolor: theme.palette.background.default,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {USER_ROLES.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Field>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                as={TextField}
                fullWidth
                label="Descrição"
                name="description"
                disabled={loading}
                sx={{
                  bgcolor: theme.palette.background.default,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Tab 2 - Informações Pessoais */}
      {tab === 2 && (
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Field
                as={TextField}
                fullWidth
                label="Data de nascimento"
                name="birth_date"
                type="date"
                disabled={loading}
                error={!!(touched.birth_date && errors.birth_date)}
                helperText={touched.birth_date && errors.birth_date}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                as={TextField}
                select
                fullWidth
                label="Gênero"
                name="gender_flag"
                disabled={loading}
                error={!!(touched.gender_flag && errors.gender_flag)}
                helperText={touched.gender_flag && errors.gender_flag}
              >
                {GENDER_OPTIONS.map((gender) => (
                  <MenuItem key={gender.value} value={gender.value}>
                    {gender.label}
                  </MenuItem>
                ))}
              </Field>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field name="phoneNumber">
                {({ field }: any) => (
                  <MaskedField
                    {...field}
                    fullWidth
                    label="Telefone"
                    mask="(99) 99999-9999"
                    disabled={loading}
                    error={!!(touched.phoneNumber && errors.phoneNumber)}
                    helperText={touched.phoneNumber && errors.phoneNumber}
                  />
                )}
              </Field>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                as={TextField}
                fullWidth
                label="CNH"
                name="cnh_number"
                disabled={loading}
                error={!!(touched.cnh_number && errors.cnh_number)}
                helperText={touched.cnh_number && errors.cnh_number}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                as={TextField}
                fullWidth
                label="Validade CNH"
                name="cnh_expiration_date"
                type="date"
                disabled={loading}
                error={!!(touched.cnh_expiration_date && errors.cnh_expiration_date)}
                helperText={touched.cnh_expiration_date && errors.cnh_expiration_date}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                as={TextField}
                select
                fullWidth
                label="Seção"
                name="section_name"
                disabled={loading || sectors.length === 0}
                helperText="Opcional"
                value={values.section_name || ''}
                onChange={(e: any) => {
                  setFieldValue('section_name', e.target.value);
                  setFieldValue('unit', '');
                }}
                sx={{
                  bgcolor: theme.palette.background.default,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value="">Selecione</MenuItem>
                {sectors.map((sector: any) => (
                  <MenuItem key={sector.id} value={sector.id}>
                    {sector.name}
                  </MenuItem>
                ))}
              </Field>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                as={TextField}
                select
                fullWidth
                label="Unidade"
                name="unit"
                disabled={loading}
                helperText="Opcional"
                value={values.unit || ''}
                onChange={(e: any) => setFieldValue('unit', e.target.value)}
                sx={{
                  bgcolor: theme.palette.background.default,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value="">Selecione</MenuItem>
                {units.length === 0 ? (
                  <MenuItem disabled value="">
                    Nenhuma unidade registrada para esta seção
                  </MenuItem>
                ) : (
                  units.map((unit: any) => (
                    <MenuItem key={unit.id} value={unit.id}>
                      {unit.number}
                    </MenuItem>
                  ))
                )}
              </Field>
            </Grid>
            {mode === 'create' && (
              <>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Senha"
                    name="password"
                    type="password"
                    required
                    disabled={loading}
                    error={!!(touched.password && errors.password)}
                    helperText={touched.password && errors.password}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Confirmar senha"
                    name="confirmPassword"
                    type="password"
                    required
                    disabled={loading}
                    error={!!(touched.confirmPassword && errors.confirmPassword)}
                    helperText={touched.confirmPassword && errors.confirmPassword}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      )}

      {/* Botões de navegação */}
      <Box sx={{ mt: 4 }}>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Box>
            {tab > 0 && (
              <Button variant="outlined" onClick={handleBack} disabled={loading}>
                Voltar
              </Button>
            )}
          </Box>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={onCancel} disabled={loading}>
              Cancelar
            </Button>
            {tab < 2 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading || !validateStep(tab)}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  '&:hover': { bgcolor: theme.palette.primary.dark },
                }}
              >
                Próximo
              </Button>
            ) : (
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !isValid}
                loading={loading}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  '&:hover': { bgcolor: theme.palette.primary.dark },
                }}
              >
                {mode === 'create' ? 'Criar usuário' : 'Salvar alterações'}
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}